import clsx from 'clsx';
import { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { IconFileExport } from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import CustomImage from '@/components/CustomImage';
import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';

interface IExportDropdownProps {
  onCsvExport?: () => void;
  onPdfExport?: () => void;
  className?: string;
  disabled?: boolean;
}

const ExportDropdown: React.FC<IExportDropdownProps> = ({
  onCsvExport,
  onPdfExport,
  className,
  disabled = false,
}) => {
  const { formatText } = useFormatText();
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!isOpen) {
      if (disabled) return;

      // Close all other dropdowns
      document.dispatchEvent(new CustomEvent('close-all-dropdowns'));

      setTimeout(() => {
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const scrollX = window.scrollX || window.pageXOffset;
          const scrollY = window.scrollY || window.pageYOffset;

          setPosition({
            top: rect.bottom + scrollY + 5,
            left: isRTL ? rect.right + scrollX : rect.left + scrollX,
          });
        }
        setIsOpen(true);
      }, 0);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        !buttonRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    const handleCloseAll = () => {
      setIsOpen(false);
    };

    if (isOpen) {
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      document.addEventListener('close-all-dropdowns', handleCloseAll);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('close-all-dropdowns', handleCloseAll);
    };
  }, [isOpen]);

  const actions = [
    {
      key: 'csv',
      label: 'CSV',
      icon: (
        <CustomImage
          src="/assets/svg/FileCSV.svg"
          alt="CSV"
          width={24}
          height={24}
        />
      ),
      onClick: onCsvExport,
    },
    {
      key: 'pdf',
      label: 'PDF',
      icon: (
        <CustomImage
          src="/assets/svg/FilePDF.svg"
          alt="PDF"
          width={24}
          height={24}
        />
      ),
      onClick: onPdfExport,
    },
  ];

  return (
    <div className={clsx('relative', className)}>
      <div ref={buttonRef} onClick={toggle}>
        <Button
          icon={<IconFileExport size={20} />}
          label={formatText('common.actions.export-data')}
          variant="primary"
          className="px-4 h-12 gap-2 min-w-33.25"
          disabled={disabled}
        />
      </div>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className={clsx(
              'fixed z-9999 flex flex-col bg-white border border-neutral-light-200 rounded-2xl shadow-lg p-2 min-w-40',
              isRTL ? '-translate-x-full' : ''
            )}
            style={{
              top: position.top,
              left: position.left,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {actions.map((action) => (
              <button
                key={action.key}
                className="flex items-center gap-3 w-full px-4 py-3 rounded-xl hover:bg-[#F9F5FF] text-neutral-primary text-base font-medium transition-colors"
                onClick={() => {
                  action.onClick?.();
                  setIsOpen(false);
                }}
              >
                <div className="flex items-center justify-center">
                  {action.icon}
                </div>
                {action.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
};

export default ExportDropdown;
